import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import {getIsAgeRestricted} from '../../../common/getIsAgeRestricted';
import {CardViewItem} from '../../../design-system/organisms/CardViewItem/CardViewItem';
import {CardViewItem as CardViewItemProps} from '../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../logging/aggregateAndSendLog';
import {useLogin} from '../../../user/useLogin.hook';
import {useGetDayOfWeekEventLog} from '../hook/useGetDayOfWeekEventLog';
import {DayOfWeekLogProps} from '../type';

interface Props {
  item: CardViewItemProps;
  columnIndex?: number;
  cardIndex?: number;
  logProps: DayOfWeekLogProps;
}

export const DayOfWeekSectionCardItem = ({
  item,
  columnIndex,
  cardIndex = 0,
  logProps,
}: Props) => {
  const {user} = useLogin();
  const {getCustomProps} = useGetDayOfWeekEventLog(logProps);
  const commonCustomProps = getCustomProps();

  const itemOrdnum = String(cardIndex + 1);
  const columnOrdnumProps = !isNil(columnIndex)
    ? {event_tab_ordnum: String(columnIndex + 1)}
    : {};

  const eventLog = item.eventLog ?? {};
  const itemCustomProps = omit(eventLog.customProps ?? {}, [
    'landing_path',
    'view_type',
  ]);
  const hasHelixId = !isNil(itemCustomProps.helix_id);
  const eventMeta = eventLog.eventMeta
    ? {
        ...eventLog.eventMeta,
        provider: hasHelixId ? 'helix' : 'service',
      }
    : undefined;

  return (
    <CardViewItem
      onInView={
        // 추천순을 파악하기 위해 helix_id 값 존재 여부를 판단한다.
        // 관련 슬랙: https://kakaopagecorp.slack.com/archives/C023RF50P7C/p1762240082986079?thread_ts=1762147992.367649&cid=C023RF50P7C
        hasHelixId
          ? () => {
              aggregateAndSendLog({
                actionType: 'ViewImp',
                actionName: '작품_노출',
                viewimp_contents: {
                  imp_id: 'songoing',
                  imp_provider: 'helix',
                  imp_type: 'vimp',
                  layer1: '작품리스트',
                  id: eventMeta?.series_id ?? '',
                  type: 'seriesId',
                  imp_ordnum: itemOrdnum,
                },
                customProps: {
                  user_action: 'imp',
                  ...commonCustomProps,
                  ...columnOrdnumProps,
                  ...(itemCustomProps as any),
                },
              });
            }
          : undefined
      }
      onClickCapture={() => {
        aggregateAndSendLog({
          actionType: 'Event',
          actionName: '작품_클릭',
          eventMeta: {
            ...eventMeta,
            id: eventMeta?.series_id ?? '',
            type: 'seriesId',
          },
          click: {
            layer1: '작품리스트',
            ordnum: itemOrdnum,
            imp_id: 'songoing',
            imp_provider: hasHelixId ? 'helix' : 'service',
          },
          customProps: {
            user_action: 'click',
            landing_path: 'contents',
            ...commonCustomProps,
            ...columnOrdnumProps,
            ...(itemCustomProps as any),
          },
        });
      }}
      categoryType={item.categoryType}
      thumbnail={item.thumbnail}
      badgeList={item.badgeList}
      statusBadge={item.statusBadge}
      statusLabelText={item.statusLabelText}
      ageGradeBadge={item.ageGradeBadge}
      subtitleList={item.subtitleList}
      cardViewTitle={item.title}
      backgroundColor={item.backgroundColor}
      isBook={item.isBook}
      isLegacy={item.isLegacy}
      scheme={item.scheme}
      imageLazyLoad={false}
      isAgeRestricted={getIsAgeRestricted({
        age: user?.age,
        authenticatedAt: user?.authenticatedAt,
        ageGrade: item.ageGrade,
        selfCensorship: item.selfCensorship,
      })}
      rank={item.rank}
      rankVariation={item.rankVariation}
      altText={item.altText}
      discountRateText={item.discountRateText}
    />
  );
};
